import { gql, useMutation } from '@apollo/client';
import { CircularProgress } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Snackbar from '@material-ui/core/Snackbar';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { graphql } from 'gatsby';
import React from 'react';
import { Languages, defaultLocation } from '../utils/getAllSupportedLanguages';
import DesktopFormContainer from '../components/DesktopFormContainer';
import DownloadTypeDialog from '../components/DownloadTypeDialog';
import MobileFormContainer from '../components/MobileFormContainer';
import PardotEmailDialog from '../components/PardotEmailDialog';
import Seo from '../components/Seo';
import TemplatePreviewDialog from '../components/TemplatePreviewDialog';
import TemplateSeoContent from '../components/Template/TemplateSeoContent';
import TemplateInfoSection from '../components/Template/TemplateInfoSection';
import WorkspaceDialog from '../components/WorkspaceDialog';
import { AuthUserContext } from '../contexts/AuthUserContext';
import { LocationLanguageContext } from '../contexts/LocationLanguageContext';
import { NovadeLiteUrlContext } from '../contexts/NovadeLiteUrlContext';
import { globalColors } from '../styles/globalColorVariables';
import {
  AuthUserContextType,
  LocationLanguageContextType,
  NovadeLiteUrlContextType,
  NovadeLiteUser,
  StrapiTemplate,
  Template,
  TemplateCategory
} from '../typings';
import { exportToPDF } from '../utils/exportPDF';
import { getTemplateCategoryByLocale } from '../utils/getFilterOptions';
import { getAttachmentData, getEmailValues, sendEmail, setEmailValues } from '../utils/sendStrapiEmail';
import { setGTMEvent } from '../utils/setGAEvent';
import { getSortedTemplate, getTemplateValues, updateTemplateCountryCode } from '../utils/templateValues';
import { matchesMobile, matchesDesktop } from '../utils/mediaQuery';
import useTemplateStyles from '../styles/templateStyle';

interface TemplatePageQueryProps {
  PublishedDateLabel: string;
  AuthorLabel: string;
  SaveToNovadeLiteLabel: string;
  locale: string;
  WebPreviewLabel: string;
  MobilePreviewLabel: string;
  DownloadPDFLabel: string;
  SuccessMessage: string;
  ErrorMessage: string;
  DownloadLabel: string;
  WorkspaceLabel: string;
  WorkspaceButton: string;
}

interface TemplateProps {
  data: {
    strapiTemplate: StrapiTemplate;
    allStrapiTemplatePage: {
      nodes: TemplatePageQueryProps[];
    };
  };
}

export const query = graphql`
  query TemplateQuery($id: Int!) {
    strapiTemplate(strapiId: { eq: $id }) {
      id
      published_at
      strapiId
      Template {
        name
        templateGroup {
          name
        }
        templateSections {
          name
          rank
          sectionFields {
            name
            rank
            isMandatory
            galleryAllowed
            textValidation
            fieldInputType
            selectOptions {
              color
              name
              rank
            }
            subFields {
              fieldInputType
              name
              rank
              selectOptions {
                color
                name
                rank
              }
            }
          }
        }
      }
      Title
      Description
      Keywords
      Author
      SeoContent
      countries {
        Localizations {
          locale
          localeName
          displayName
        }
        name
      }
    }
    allStrapiTemplatePage {
      nodes {
        PublishedDateLabel
        AuthorLabel
        SaveToNovadeLiteLabel
        locale
        WebPreviewLabel
        MobilePreviewLabel
        DownloadPDFLabel
        SuccessMessage
        ErrorMessage
        DownloadLabel
        WorkspaceLabel
        WorkspaceButton
      }
    }
  }
`;

const TemplatePage: React.FC<TemplateProps> = ({ data }) => {
  const classes = useTemplateStyles();
  const template = data.strapiTemplate;
  const templateDetails: Template = getSortedTemplate({ ...template.Template, ...{ id: template.id } });
  const templateLanguage = template.template_language || Languages.English;
  const [openWorkspace, setOpenWorkspace] = React.useState(false);
  const [openDownloadType, setOpenDownloadType] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [openTemplatePreview, setOpenTemplatePreview] = React.useState(true);
  const [openEmail, setOpenEmail] = React.useState(false);
  const [snackBarMsg, setSnackBarMsg] = React.useState<string | undefined>('');
  const [userData, setUserData] = React.useState<NovadeLiteUser>();
  const authUser = React.useContext<AuthUserContextType | null>(AuthUserContext);
  const locationLanguage = React.useContext<LocationLanguageContextType | null>(LocationLanguageContext);
  const language = locationLanguage ? locationLanguage.language : Languages.English;
  const country = locationLanguage?.country ?? defaultLocation;
  const contextUrl = React.useContext<NovadeLiteUrlContextType | null>(NovadeLiteUrlContext);
  const templatePageContent = data.allStrapiTemplatePage.nodes.find((x: any) => x.locale === language);

  let countries: TemplateCategory[] | null = [];
  if (template.countries) {
    countries = getTemplateCategoryByLocale(template.countries, language);
  }

  const openWorkspaceDialog = () => {
    setOpenWorkspace(true);
  };
  const openDownloadTypeDialog = (inputUserData: NovadeLiteUser) => {
    setUserData(inputUserData);
    setOpenDownloadType(true);
  };
  const openEmailDialog = () => {
    if (authUser && authUser.user) {
      openDownloadTypeDialog(authUser.user);
    } else {
      setOpenEmail(true);
    }
  };
  const handlePreviewClose = () => {
    setOpenTemplatePreview(false);
  };

  const exportPDF = () => {
    const templateValues = getTemplateValues();
    updateTemplateCountryCode(country.locationCode);
    const pdfEndpoint = `${contextUrl?.novadeLiteUrl}/portal-template-pdf`;
    exportToPDF(pdfEndpoint, templateValues).then((fileData) => {
      const attachmentData = getAttachmentData(fileData.fileContent, fileData.fileName, fileData.fileType);
      setEmailValues(userData!, language, template, attachmentData!);
      const emailMsg = getEmailValues();
      sendEmail(emailMsg);
    });
    setGTMEvent('Download Template as PDF', 'download_PDF', templateValues.name);
  };

  const handleDialogClose = (isSuccessfulSave: any | undefined) => {
    setOpenWorkspace(false);
    if (typeof isSuccessfulSave !== 'undefined') {
      if (isSuccessfulSave) {
        setSnackBarMsg(templatePageContent?.SuccessMessage);
      } else {
        setSnackBarMsg(templatePageContent?.ErrorMessage);
      }
      setOpenSnackbar(true);
    }
  };

  const handleDownloadTypeClose = () => {
    setOpenDownloadType(false);
    exportPDF();
  };

  const handleEmailDialogClose = (isSuccessfulSubmit: boolean | undefined, inputEmail: string | undefined) => {
    setOpenEmail(false);
    if (isSuccessfulSubmit) {
      const inputUserData: NovadeLiteUser = {
        email: inputEmail!,
        displayName: '' // guest
      };
      openDownloadTypeDialog(inputUserData);
    }
  };
  const handleSnackbarClose = (_: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  // to save directly to Novade Lite without opening Workspace Dialog if user only has 1 workspace
  const UPSERT_TEMPLATE_DATE = gql`
    mutation upsertTemplateData($template: TemplatePortalInput!) {
      savePortalTemplateToWorkspace(template: $template)
    }
  `;
  const databasesMap = contextUrl ? authUser?.databasesMap[contextUrl.novadeLiteUrl] || {} : {};
  const [upsertTemplateData, { loading }] = useMutation<{ savePortalTemplateToWorkspace: any }, { template: Template }>(
    UPSERT_TEMPLATE_DATE,
    {
      variables: {
        template: templateDetails
      },
      context: {
        uri: `${contextUrl?.novadeLiteUrl}/graphql`,
        headers: {
          Authorization: Object.values(databasesMap).length !== 0 ? `Bearer ${Object.values(databasesMap)[0].token}` : 'No Auth'
        }
      },
      onCompleted({ savePortalTemplateToWorkspace }) {
        if (savePortalTemplateToWorkspace) {
          setGTMEvent('Save template to Novade Lite workspace', 'save_To_Novade_Lite', templateDetails.name);
          setSnackBarMsg(templatePageContent?.SuccessMessage);
          setOpenSnackbar(true);
        }
      },
      onError({ graphQLErrors, networkError }) {
        if (graphQLErrors || networkError) {
          setSnackBarMsg(templatePageContent?.ErrorMessage);
          setOpenSnackbar(true);
        }
      }
    }
  );

  const isMobile = matchesMobile();
  const isDesktop = matchesDesktop();
  const templateInfoProps = {
    isMobile,
    templateTitle: template.Title,
    authorLabel: templatePageContent?.AuthorLabel,
    author: template.Author,
    description: template.Description,
    publishedDateLabel: templatePageContent?.PublishedDateLabel,
    publishedDate: template.published_at
  };

  return (
    <>
      {templatePageContent ? (
        <>
          <Seo
            title={template.Title}
            description={template.Description}
            keywords={template.Keywords}
            pathname={`/template/${template.Slug}`}
          />
          <Container>
            <br />
            {isMobile ? (
              <Grid container spacing={1}>
                <TemplateInfoSection {...templateInfoProps} />
              </Grid>
            ) : (
              <Grid container spacing={1}>
                <TemplateInfoSection {...templateInfoProps} />
                <Grid item xs={12} md={1} />
                <Grid item xs={12} md={3}>
                  <div className={classes.actionButtonsContainer}>
                    <Button variant='contained' color='secondary' className={classes.secondaryButton} onClick={openEmailDialog}>
                      {templatePageContent.DownloadLabel}
                    </Button>
                    {Object.values(databasesMap).length === 1 ? (
                      <Button
                        style={{ float: 'right' }}
                        variant='contained'
                        color='primary'
                        disabled={!authUser?.user || loading}
                        onClick={() => upsertTemplateData()}
                      >
                        {!loading ? (
                          templatePageContent.SaveToNovadeLiteLabel
                        ) : (
                          <div>
                            <CircularProgress style={{ height: '1rem', width: '1rem', color: 'white' }} />
                          </div>
                        )}
                      </Button>
                    ) : (
                      <Button
                        style={{ float: 'right' }}
                        variant='contained'
                        color='primary'
                        disabled={!authUser?.user}
                        onClick={openWorkspaceDialog}
                      >
                        {templatePageContent.SaveToNovadeLiteLabel}
                      </Button>
                    )}
                    <DownloadTypeDialog open={openDownloadType} onDialogClose={handleDownloadTypeClose} />
                    <PardotEmailDialog open={openEmail} templateTitle={template.Title} onDialogClose={handleEmailDialogClose} />
                  </div>
                </Grid>
              </Grid>
            )}
            <br />
            <Grid container spacing={1}>
              <Grid item xs={12} md={8}>
                <Paper>
                  <DesktopFormContainer templateDetails={templateDetails} templateLanguage={templateLanguage} />
                </Paper>
              </Grid>
              <Grid item xs={12} md={1} />
              <Grid item xs={12} md={3}>
                {!isMobile && <MobileFormContainer templateDetails={templateDetails} templateLanguage={templateLanguage} />}
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <TemplateSeoContent isMobile={isMobile} seoContent={template.SeoContent} textColor='#000' />
            </Grid>
            <WorkspaceDialog
              open={openWorkspace}
              onDialogClose={handleDialogClose}
              template={templateDetails}
              selectLabel={templatePageContent.WorkspaceLabel}
              btnLabel={templatePageContent.WorkspaceButton}
            />
            <Snackbar
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}
              open={openSnackbar}
              onClose={handleSnackbarClose}
              autoHideDuration={5000}
              message={snackBarMsg}
            />
          </Container>
          {isDesktop && template && openTemplatePreview && (
            <TemplatePreviewDialog open={openTemplatePreview} template={template} onDialogClose={handlePreviewClose} isMobile={isMobile} />
          )}
          {isMobile && (
            <AppBar position='sticky' color='primary' className={classes.mobileFooter}>
              <Toolbar className={classes.mobileFooterToolbar}>
                {Object.values(databasesMap).length === 1 ? (
                  <Button disabled={!authUser?.user || loading} onClick={() => upsertTemplateData()}>
                    {!loading ? (
                      templatePageContent.SaveToNovadeLiteLabel
                    ) : (
                      <div>
                        <CircularProgress style={{ height: '1rem', width: '1rem', color: globalColors.disabledColor }} />
                      </div>
                    )}
                  </Button>
                ) : (
                  <Button disabled={!authUser?.user} onClick={openWorkspaceDialog}>
                    {templatePageContent.SaveToNovadeLiteLabel}
                  </Button>
                )}
                <Button color='primary' onClick={openEmailDialog}>
                  {templatePageContent.DownloadLabel}
                </Button>
                <DownloadTypeDialog open={openDownloadType} onDialogClose={handleDownloadTypeClose} />
                <PardotEmailDialog open={openEmail} templateTitle={template.Title} onDialogClose={handleEmailDialogClose} />
              </Toolbar>
            </AppBar>
          )}
        </>
      ) : (
        <Typography variant='body1' />
      )}
    </>
  );
};
export default TemplatePage;
